import { useState } from "react";
import { log } from "../../commonfunctions/logger";
import {
  TransactionType,
  transactionTypes,
  TransactionResponseTypes,
  transactionSearchTypes,
  TransactionSearchType,
} from "./types";
import Button, { ButtonType } from "../../components/Button/Button";
import { Container, StyledInput, StyledSelect } from "../../components/styled/styled-components";
import { useDocumentTitle } from "../../hooks/document-title";
import { TransactionSummary } from "../../interfaces/interfaces";
import TransactionCard from "./TransactionCard/TransactionCard";
import { getDateRangeByDays, formatDateToString } from "./util";
import getTransactions from "./getTransactios";
import DatePicker from "../../components/DatePicker/DatePicker";
import getTransactionsByPO from "./getTransactionByPO";
import Tooltip from "../../components/Tooltip/Tooltip";
import ExclamationCirlce from "../../assets/exclamationCircle";
import Alert from "../../components/Alert/Alert";

const DEFAULT_DAYS_RANGE = 7;
const MAX_MONTH_RANGE = 2;

const Transactions = () => {
  useDocumentTitle("Previous transactions");
  const [transactionType, setTransactionType] = useState<TransactionType>("All");
  const [transactionSearchType, setTransactionSearchType] =
    useState<TransactionSearchType>("Search");
  const [transactions, setTransactions] = useState<TransactionSummary[] | null>(null);
  const [message, setMessage] = useState("No Transactions.");
  const [isLoading, setIsLoading] = useState(false);
  const { startDate: defaultStartDate, endDate: defaultEndDate } =
    getDateRangeByDays(DEFAULT_DAYS_RANGE);

  const currentDate = new Date();
  const [startDate, setStartDate] = useState<Date>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date>(defaultEndDate);
  const [maxEndDate, setMaxEndDate] = useState<Date>(currentDate);

  const [searchValue, setSearchValue] = useState<string>("");

  const loadTransactions = async () => {
    setIsLoading(true);
    const NZStartDate = formatDateToString(startDate);
    const NZEndDate = formatDateToString(endDate);
    const response = await getTransactions(transactionType, NZStartDate, NZEndDate);
    if (response.type == TransactionResponseTypes.SUCCESS) {
      setTransactions(response.transactions || []);
      setMessage("No Transactions.");
    } else {
      setTransactions([]);
      setMessage(response.message || "No transactions found");
      log(new Error(), "Failed to load transaction");
    }
    setIsLoading(false);
  };

  const loadTransactionsPO = async () => {
    setIsLoading(true);
    const response = await getTransactionsByPO(searchValue);
    if (response.type == TransactionResponseTypes.SUCCESS) {
      setTransactions(response.transactions || []);
      setMessage("No Transactions.");
    } else {
      setTransactions([]);
      setMessage(response.message || "No transactions found");
      log(new Error(), "Failed to load transaction");
    }
    setIsLoading(false);
  };

  const onChangeStartDate = (newStartDate: Date) => {
    let newMaxEndDate = new Date(newStartDate);
    newMaxEndDate.setMonth(newStartDate.getMonth() + MAX_MONTH_RANGE);

    if (newMaxEndDate > currentDate) {
      newMaxEndDate = currentDate;
    }

    if (endDate > newMaxEndDate) {
      setEndDate(newMaxEndDate);
    }
    setStartDate(newStartDate);
    setMaxEndDate(newMaxEndDate);
  };

  return (
    <Container className="transactions-page">
      <div className="transactions-header">
        <h2>Transactions</h2>
        <div className="transactions-search-type">
          <label htmlFor="transactionSearchType">Search Type:</label>
          <StyledSelect
            name="transactionSearchType"
            id="transactionSearchType"
            onChange={(e) => setTransactionSearchType(e.target.value as TransactionSearchType)}
            value={transactionSearchType}
          >
            {transactionSearchTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </StyledSelect>
        </div>
      </div>

      {transactionSearchType === "Search" && (
        <div className="transactions-search-section">
          <div className="transactions-form-group transactions-form-group--po">
            <label htmlFor="poNumber">PO Number:</label>
            <StyledInput
              id="poNumber"
              type="text"
              placeholder="Enter PO Number"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSearchValue(event.target.value)
              }
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "Enter" && searchValue.trim() && !isLoading) {
                  loadTransactionsPO();
                }
              }}
              role="searchbox"
              value={searchValue}
              className="transactions-po-input"
            />
          </div>
          <Button
            type={ButtonType.primary}
            onClick={loadTransactionsPO}
            isLoading={isLoading}
            disabled={!searchValue.trim()}
          >
            Search Transactions
          </Button>
        </div>
      )}

      {transactionSearchType === "Date Time" && (
        <div className="transactions-date-section">
          <div className="transactions-form-group">
            <label htmlFor="transactionType">Transaction Type:</label>
            <StyledSelect
              id="transactionType"
              name="transactionType"
              onChange={(e) => setTransactionType(e.target.value as TransactionType)}
              value={transactionType}
            >
              {transactionTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </StyledSelect>
          </div>

          <div className="transactions-form-group">
            <label htmlFor="startDate">Start Date:</label>
            <DatePicker selectedDate={startDate} maxDate={endDate} onChange={onChangeStartDate} />
          </div>

          <div className="transactions-form-group">
            <label htmlFor="endDate">End Date:</label>
            <div className="transactions-date-input-wrapper">
              <DatePicker
                selectedDate={endDate}
                maxDate={maxEndDate}
                minDate={startDate}
                onChange={(date) => setEndDate(date)}
              />
              <Tooltip
                tooltipClassName="tooltip"
                content={
                  "Date searches are limited; setting the start date too far back will automatically reduce the end date."
                }
                position="right"
              >
                <ExclamationCirlce className="transactions-info-icon" />
              </Tooltip>
            </div>
          </div>

          <Button type={ButtonType.primary} onClick={loadTransactions} isLoading={isLoading}>
            Load Transactions
          </Button>
        </div>
      )}

      <Alert>Please Note: Transactions are not in the system until the next day.</Alert>

      <div className="transactions-results">
        {transactions && transactions.length > 0 ? (
          <table className="transactions-table">
            <tbody>
              <tr className="transactions-table__header">
                <th className="transaction-header--number">Transaction No.</th>
                <th className="transaction-header--center">Type</th>
                <th className="transaction-header--center">Date</th>
                <th className="transaction-header--center">Status</th>
                <th className="transaction-header--left">Order</th>
                <th className="transaction-header--right">Debit</th>
                <th className="transaction-header--right">Actions</th>
              </tr>
              {transactions.map((transaction, index) => (
                <tr key={index} className="transaction-row">
                  <TransactionCard transaction={transaction} />
                </tr>
              ))}
            </tbody>
          </table>
        ) : transactions !== null ? (
          <div className="transactions-empty">
            <h3>{message}</h3>
          </div>
        ) : null}
      </div>
    </Container>
  );
};

export default Transactions;
